import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Select, Button, Alert, useIsMobile, COMPONENT_LABELS } from '../components/ui';
import LocationSelect from '../components/ui/LocationSelect';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';

const URGENCY = [
  { value: 'critical', label: 'Critical', desc: 'Life-threatening · <6h', color: 'var(--red)',   bg: 'var(--red-light)' },
  { value: 'high',     label: 'High',     desc: 'Needed within 24h',       color: 'var(--amber)', bg: 'var(--amber-light)' },
  { value: 'normal',   label: 'Normal',   desc: 'Planned / scheduled',      color: 'var(--green)', bg: 'var(--green-light)' },
];

export default function NewRequest() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    blood_type: 'O+', component: 'whole_blood', units_needed: 1, urgency: 'normal',
    patient_name: '', hospital_name: '', hospital_address: '', notes: '',
  });
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.hospital_name) { setError('Hospital name is required.'); return; }
    // Without a city there are no coordinates, and without coordinates we can't
    // work out which donors are nearby — so this isn't an optional field.
    if (!place) { setError('Select the country, region and city of the hospital.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/requests/', {
        ...form,
        units_needed: Number(form.units_needed),
        city_id: place.city_id,
      });
      navigate('/requests');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to post request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div style={{ animation: 'bb-fadeUp 0.5s ease both' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' }}>Post a Blood Request</h1>
        <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Matching donors are notified immediately.</p>
      </div>

      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0 14px' }}>
          <Select label="Blood Type Needed" value={form.blood_type} onChange={set('blood_type')}>
            {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(t => <option key={t}>{t}</option>)}
          </Select>
          <Select label="What's Needed" value={form.component} onChange={set('component')}>
            {Object.entries(COMPONENT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Select>
          <Input label="Units Needed" type="number" min="1" max="10" value={form.units_needed} onChange={set('units_needed')} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '8px' }}>Urgency Level</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: '8px' }}>
            {URGENCY.map(o => (
              <div
                key={o.value}
                onClick={() => setForm(f => ({ ...f, urgency: o.value }))}
                style={{
                  padding: '13px 14px', borderRadius: '9px', cursor: 'pointer',
                  border: `2px solid ${form.urgency === o.value ? o.color : 'var(--gray-200)'}`,
                  background: form.urgency === o.value ? o.bg : 'var(--white)',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: form.urgency === o.value ? o.color : 'var(--gray-800)', marginBottom: '2px' }}>
                  {o.label}
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--gray-500)' }}>{o.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <Input label="Patient Name (optional)" placeholder="e.g. Priya Sharma" value={form.patient_name} onChange={set('patient_name')} />
        <Input label="Hospital Name *" placeholder="e.g. AIIMS Delhi" value={form.hospital_name} onChange={set('hospital_name')} />

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0 14px' }}>
          <LocationSelect onChange={setPlace} required />
        </div>
        <Input label="Hospital Address" placeholder="Ward / Block info" value={form.hospital_address} onChange={set('hospital_address')} />
        <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '-10px', marginBottom: '16px' }}>
          The more precise the address, the more accurately we can reach donors near this hospital.
        </p>

        <Input label="Notes" placeholder="Any additional info for donors..." value={form.notes} onChange={set('notes')} />

        {error && <Alert variant="error">{error}</Alert>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexDirection: isMobile ? 'column-reverse' : 'row', marginTop: '4px' }}>
          <Button variant="secondary" onClick={() => navigate('/requests')} style={isMobile ? { width: '100%' } : {}}>Cancel</Button>
          <Button variant="primary" loading={loading} onClick={handleSubmit} style={isMobile ? { width: '100%' } : {}}>Post Request</Button>
        </div>
      </Card>
      </div>
    </PageLayout>
  );
}
